import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, StatusPill } from '@elbklar/ui';
import type { DashboardData } from '@/lib/api';
import { TaskCard } from '@/features/tasks/components/TaskCard';
import { AufgabeBearbeiten } from '@/features/tasks/components/AufgabeBearbeiten';
import { useAufgabeErledigen } from '@/features/tasks/useAufgabeErledigen';

interface TasksWidgetProps {
  tasks: DashboardData['tasks'];
}

/**
 * „Meine Aufgaben“ in der Übersicht am Rechner: dieselbe Zeile wie überall
 * (TaskCard), mit Erledigen, Fälligkeit, Priorität im festen Wortlaut und
 * dem Stift zum Bearbeiten.
 *
 * Nicht die ganze Zeile als Klickfläche: Sie ist `checkable`, und `ListRow`
 * schliesst `onClick` damit aus — die runde Checkbox wäre ein Klickziel im
 * Klickziel. Eine Aufgabe hakt man ab, einen Eintrag öffnet man.
 */
export function TasksWidget({ tasks }: TasksWidgetProps) {
  const allTasks = [...tasks.overdue, ...tasks.today].slice(0, 5);
  const erledigen = useAufgabeErledigen();
  const [bearbeiteteAufgabe, setBearbeiteteAufgabe] = useState<string | null>(null);
  const schliessen = useCallback(() => setBearbeiteteAufgabe(null), []);

  return (
    <Card
      title="Meine Aufgaben"
      flush
      actions={
        <Button asChild size="sm" variant="ghost">
          <Link to="/tasks">Alle anzeigen</Link>
        </Button>
      }
    >
      {/* Summary Stats */}
      <div className="flex flex-wrap gap-4 border-b border-border px-5 py-3">
        {tasks.overdue.length > 0 && (
          <StatusPill tone="danger">
            {tasks.overdue.length} Überfällig
          </StatusPill>
        )}
        <StatusPill tone="warning">{tasks.today.length} Heute</StatusPill>
        <StatusPill tone="neutral">{tasks.total_open} Offen</StatusPill>
      </div>

      {/* Task List */}
      {allTasks.length > 0 ? (
        <div>
          {allTasks.map((task) => (
            <TaskCard key={task.id} task={task} onStatusChange={erledigen} onEdit={(a) => setBearbeiteteAufgabe(a.id)} />
          ))}
        </div>
      ) : (
        <p className="py-6 text-center t-small text-muted-foreground">Keine offenen Aufgaben</p>
      )}

      <AufgabeBearbeiten aufgabeId={bearbeiteteAufgabe} onClose={schliessen} />
    </Card>
  );
}
